import { useState } from "react";
import { useInView } from "react-intersection-observer";
import { RouteFactory } from "@app/routePaths";
import { HomeContext } from "@app/types/Page";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import HomepageMasthead from "@components/Header/HomepageMasthead";
import MetaTags from "@components/MetaTags/MetaTags";
import MyBTHomeCarousel from "@components/MyBT/MyBTHomeCarousel";
import HomepageCarouselNewsletter from "@components/Newsletter/HomepageCarouselNewsletter";
import { NEWSLETTER_TYPE } from "@components/Newsletter/types/Newsletter";
import HomeSkybox from "@components/Skybox/HomeSkybox";
import { myBTRecommendedKeywords } from "@features/mybt/constants";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useHomepageRedirect } from "@hooks/useHomepageRedirect";
import SubMobileNavigation from "@particles/Navigation/components/SubMobileNavigation";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { IS_PLAYWRIGHT } from "@util/constant";
import { gaEventTracker, getHomePageJSONLD } from "@util/helpers";
import { useDetectAdBlock } from "adblock-detect-react";

import Asean from "./Asean/Asean";
import BreakingNews from "./BreakingNews/BreakingNews";
import CompaniesMarkets from "./CompaniesMarkets/CompaniesMarkets";
import Lifestyle from "./Lifestyle/Lifestyle";
import OpinionFeatures from "./OpinionFeatures/OpinionFeatures";
import Overview from "./Overview/Overview";
import Podcasts from "./Podcasts/Podcasts";
import Video from "./Video/Video";

type HomeContentProps = Pick<HomeContext, "data">;

export default function HomeContent({
  data: {
    title,
    overview,
    breakingNews,
    companiesMarkets,
    opinionFeatures,
    verticals,
    lifestyle,
    lifestyleCarousel,
    podcasts,
    myBTData,
    skyboxArticles,
    seoFooterData,
  },
}: HomeContentProps): React.ReactElement {
  useHomepageRedirect();

  const [myBTVisible, setMyBTVisible] = useState(true);

  const adBlockDetected = useDetectAdBlock();
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);

  const { ref: myBTRef, inView: myBTBlock } = useInView();

  if (myBTBlock && myBTVisible) {
    gaEventTracker("mybt", "visible", window.location.href);
    setMyBTVisible(false);
  }

  const isUserLogin = OKTAUserInfo?.loginid;

  return (
    <div className="w-full" data-testid="home-content-component">
      <AdSettings
        adNames={["lb1", "imu2", "prestitial", "catfish", "abm"]}
        path="/homepage"
        adTargets={[{ key: "page", value: "home" }]}
      />

      <MetaTags
        title={title}
        ogTitle={title}
        description="The Business Times - Singapore's leading business and financial news source. Find latest business news, market analysis, and economic insights from Singapore and Asia-Pacific."
        ogDescription="The Business Times - Singapore's leading business and financial news source. Find latest business news, market analysis, and economic insights from Singapore and Asia-Pacific."
        ogType="website"
        articlePageclass="frontpage"
        slug="/"
        keywords={[
          "singapore budget 2015",
          "singapore",
          "budget 2015",
          "business & financial news",
        ]}
        jsonLd={getHomePageJSONLD()}
        alternates={[
          { hrefLang: "en-sg", href: "https://www.businesstimes.com.sg/" },
          { hrefLang: "en", href: "https://www.businesstimes.com.sg/global" },
          { hrefLang: "x-default", href: "https://www.businesstimes.com.sg/" },
        ]}
        ogLocale="en_SG"
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header isFromHome isDisplayEditionSwitcher />

      <HomepageMasthead />

      <GAData
        title="BT Homepage"
        level2="home"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <PulseEntry />

      <Container rootClassName="pt-1 text-gray-850">
        <Row>
          <Column rootClassName="mb-1 mx-auto lg:hidden ">
            <SubMobileNavigation />
          </Column>

          <Column rootClassName="w-full mb-10">
            <Overview data={overview} testId="homepage-top-stories" />
          </Column>

          <Column rootClassName="w-full mb-10">
            <HomeSkybox articles={skyboxArticles} />
          </Column>

          <>
            {breakingNews.length > 0 ? (
              <Column rootClassName="w-full mb-10">
                <BreakingNews
                  data={breakingNews}
                  rootClassName="border-t border-black pt-3"
                  testId="homepage-breaking-news"
                />
              </Column>
            ) : null}
          </>

          <>
            {isUserLogin ? (
              <Column rootClassName="w-full mb-10">
                <div ref={myBTRef}>
                  <MyBTHomeCarousel
                    results={myBTData}
                    keywords={myBTRecommendedKeywords}
                    title="INTRODUCING"
                    showTooltip={false}
                    rootClassName="border-t pt-3 border-black"
                    disablePaywall={
                      OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
                    }
                    testId="homepage-mybt"
                    header={
                      <BlockTitle
                        text="MYBT"
                        link={RouteFactory.myAccount}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                          const target = e.target as HTMLAnchorElement;
                          const label = target.href
                            ? `see more - ${target.href}`
                            : "see more";
                          gaEventTracker("mybt", "click", label);
                        }}
                      />
                    }
                    refCode="homepage-mybt"
                  />
                </div>
              </Column>
            ) : null}
          </>

          <Container>
            <Row>
              <div className="mx-3 mb-3 h-[1px] w-full bg-gray-850"></div>
              <Column rootClassName="w-full lg:pr-6 lg:w-8/12 mb-10 lg:border-r border-gray-175 ">
                <CompaniesMarkets
                  data={companiesMarkets}
                  testId="homepage-companies-markets"
                />
              </Column>

              <Column
                rootClassName="w-full lg:w-4/12 mb-10"
                data-testid="homepage-st-stocks"
              >
                {IS_PLAYWRIGHT ? (
                  <div className="flex h-[537px] w-full items-center justify-center border-gray-700 bg-gray-500 text-gray-700">
                    ST INDEX
                  </div>
                ) : (
                  <iframe
                    className="w-full lg:pl-3"
                    src="https://sph.shareinvestor.com/bt_stock_chart_frame_2.html"
                    title="BT Stock Chart"
                    loading="lazy"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    width="100%"
                    height={537}
                  ></iframe>
                )}
              </Column>
            </Row>
          </Container>

          <Column rootClassName="w-full mb-10">
            <OpinionFeatures
              data={opinionFeatures}
              rootClassName={"border-t border-black pt-3"}
              testId="homepage-opinion-features"
            />
          </Column>
        </Row>
      </Container>

      <div className="mb-10 w-full">
        <Container>
          <Asean data={verticals} testId="homepage-verticals" />
        </Container>
      </div>

      <Container>
        <Row>
          <div className="mx-3 mb-3 h-[1px] w-full bg-gray-850"></div>
          <Column rootClassName="w-full lg:pr-6 lg:w-8/12 mb-10 lg:border-r border-gray-175">
            <Podcasts data={podcasts} testId="homepage-podcasts" />
          </Column>

          <Column rootClassName="w-full lg:w-4/12 mb-10 lg:pl-6">
            <div className="mb-3 block h-[1px] w-full bg-gray-850 lg:hidden"></div>
            <Video testId="homepage-videos" />
          </Column>
        </Row>
      </Container>

      <Container>
        <Row rootClassName="mb-7">
          <div className="mx-3 mb-3 h-[1px] w-full bg-gray-850"></div>
          <Column rootClassName="w-full">
            <HomepageCarouselNewsletter
              rootClassName="mb-6"
              testId="homepage-carousel-newsletter-component"
              newsletterToSkip={NEWSLETTER_TYPE.BTGLOBAL}
            />
          </Column>
        </Row>
      </Container>

      <div className="mb-10 w-full bg-gray-850 py-10">
        <Container>
          <Lifestyle
            data={lifestyle}
            carouselData={lifestyleCarousel}
            testId="homepage-lifestyle-carousel"
          />
        </Container>
      </div>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
}
